import angular from 'angular';
import _ from 'lodash';
import BaseAccountController from '../../lib/controllers/BaseAccountController';
import queryParameterParser from '../../lib/helpers/queryStringParser';

declare var LtFramework: lt.Static;

export default class ManageCommunicationPreferencesController extends BaseAccountController {
	public static $inject: string[] = ['$scope', '$q', '$window', 'CommunicationChannelFactory', 'CommunicationSummaryFactory',
			'CommunicationPreferenceFactory', 'CommunicationValueFactory', 'SendConfirmationCodeFactory', 'UserResource'];

	public commValues: CommPrefsApi.ICommunicationValueResource[];
	public commValueMap: ICommValueMap;
	public confirmationCode?: string;
	public generalError: string;
	public generalMessage: string;
	public loading = true;
	public prevCommValueMap: ICommValueMap;
	public partyId: string;
	public saving = false;
	public channels: CommPrefsApi.ICommunicationChannel[];
	public summary: CommPrefsApi.ICommunicationSummaryResource;
	public textErrorMessage: string;
	public textSuccessMessage: string;
	public textScreen = 'view';

	public isUnauthenticatedView: boolean;
	public emailAddressToManage: string | null;
	public action: string | null = 'action';
	public commTypeSlug: string | null;
	public commTypeIdToUnselect: number;
	public token: string | null;
	public hash: string | null;
	public exactTargetCid: string | null;
	public exactTargetRid: string | null;
	public commTypeToUnsubscribe: CommPrefsApi.ICommunicationType;
	public commTypesToHide: number[];

	private readonly $q: ng.IQService;
	private readonly CommunicationChannelFactory: CommPrefsApi.ICommunicationChannelFactory;
	private readonly CommunicationPreferenceFactory: CommPrefsApi.ICommunicationPreferenceFactory;
	private readonly CommunicationSummaryFactory: CommPrefsApi.ICommunicationSummaryFactory;
	private readonly CommunicationValueFactory: CommPrefsApi.ICommunicationValueFactory;
	private readonly SendConfirmationCodeFactory: CommPrefsApi.ISendConfirmationCodeFactory;
	private readonly UserFactory: MyAccountApi.IUserResource;

	constructor(
			$scope: ng.IScope,
			$q: ng.IQService,
			$window: ng.IWindowService,
			CommunicationChannelFactory: CommPrefsApi.ICommunicationChannelFactory,
			CommunicationSummaryFactory: CommPrefsApi.ICommunicationSummaryFactory,
			CommunicationPreferenceFactory: CommPrefsApi.ICommunicationPreferenceFactory,
			CommunicationValueFactory: CommPrefsApi.ICommunicationValueFactory,
			SendConfirmationCodeFactory: CommPrefsApi.ISendConfirmationCodeFactory,
			UserFactory: MyAccountApi.IUserResource) {
		super($scope);

		this.$q = $q;
		this.CommunicationChannelFactory = CommunicationChannelFactory;
		this.CommunicationSummaryFactory = CommunicationSummaryFactory;
		this.CommunicationPreferenceFactory = CommunicationPreferenceFactory;
		this.CommunicationValueFactory = CommunicationValueFactory;
		this.SendConfirmationCodeFactory = SendConfirmationCodeFactory;
		this.UserFactory = UserFactory;

		// if an emailAddress query parameter is provided, we load preferences around that and not the SSO ID.
		this.emailAddressToManage = queryParameterParser.getParameterByName($window, 'emailAddress');
		this.action = queryParameterParser.getParameterByName($window, 'action');
		this.commTypeSlug = queryParameterParser.getParameterByName($window, 'commTypeSlug');
		this.commTypeIdToUnselect = parseInt(queryParameterParser.getParameterByName($window, 'commTypeIdToUnselect') || '', 10);
		this.token = queryParameterParser.getParameterByName($window, 'token');
		this.hash = queryParameterParser.getParameterByName($window, 'hash');
		this.exactTargetCid = queryParameterParser.getParameterByName($window, 'et_cid');
		this.exactTargetRid = queryParameterParser.getParameterByName($window, 'et_rid');

		if (this.emailAddressToManage) {
			this.isUnauthenticatedView = true;

			// We shouldn't allow management unless a token or hash have been provided.
			// That's how we do security in lieu of an SSO ID
			if (!this.token && !this.hash) {
				this.generalError = 'Missing token or hash parameter on URL';
				this.loading = false;
				return;
			}

			this.loadDataByEmailAddress();
		} else if (this.action === 'unsubscribe') {
			this.isUnauthenticatedView = true;

			// We currently only support showing an Unsubscribe view for email addresses
			this.generalError = 'Missing emailAddress parameter on URL';
			this.loading = false;
			return;
		}
	}

	public onEditTextScreenAction(action: string, verifyForm: ng.IFormController): void {
		this.textErrorMessage = '';
		this.textSuccessMessage = '';

		if (action === 'save') {
			// validation
			if (this.commValueMap.text!.SelectedTextPhoneNumber && !this.confirmationCode) {
				this.textErrorMessage = 'Oops! You\'ve entered the wrong code, please try again.'
					+ ' Click on "Resend Code" if you still have not received a code.';
				return;
			}

			this.saving = true;

			const prevTextPhoneNumber = this.commValueMap.text!.TextPhoneNumber;
			const prevPhoneNumbers = this.commValueMap.text!.PhoneNumbers;
			this.commValueMap.text!.TextPhoneNumber = this.commValueMap.text!.SelectedTextPhoneNumber;
			// If SelectedTextPhoneNumber is not null, we are opting in. Otherwise they are selecting "none" and they should be opted out.
			const optingIn = (this.commValueMap.text?.SelectedTextPhoneNumber ?? false) && true;
			// optingIn is the opposite of what Unsubscribed is in this case.
			this.commValueMap.text!.Unsubscribed = !optingIn;
			// When optingIn is false, this removes the Unsubscribe from all Life Time TEXT communications checkbox so the user can't try to opt in/out without a phone number.
			this.commValueMap.text!.CanBeUnsubscribed = optingIn;

			this.saveCommunicationValue('text').then((commValue) => {
				const promises = new Array<ng.IPromise<{}>>();
				// Currently one text channel is all we have.
				const chan = this.getAllChannelsFromSummary().find(f => f.Key === 'text');
				const pref = chan?.Preference;
				// Similar to above, optingIn is now decided at this scope if we have a TextPhoneNumber or not.		
				const optingIn = (commValue.TextPhoneNumber ?? false) && true;

				if (chan && pref) {
					pref.OptIn = optingIn;
					const optIn = chan.Preference && chan.Preference.OptIn || false;
					if (!pref.Id) {
						// No preference Id exists, creating one
						promises.push(
							this.CommunicationPreferenceFactory.create(
								this.getServiceUpdateParams()!,
								pref,
								(newPref: CommPrefsApi.ICommunicationPreference) => {
									chan.Preference = newPref;
									chan.prevOptIn = optIn;
								}
							).$promise);
					}
					else {
						// Preference Id exists, updating it
						promises.push(
							this.CommunicationPreferenceFactory.update(
								_.extend({ id: chan.Preference!.Id }, this.getServiceUpdateParams()),
								pref,
								(newPref: CommPrefsApi.ICommunicationPreference) => {
									chan.prevOptIn = optIn;
								}
							).$promise);
					}
				}
				this.$q.all(promises).then(() => {
					// The PhoneNumbers array isn't always returned, specifically in the case where the value is changed to none,
					// so we manually copy them over here too.
					this.commValueMap.text!.PhoneNumbers = prevPhoneNumbers;
					this.saving = false;
					this.textScreen = 'view';
				}, () => {
					this.saving = false;
					this.generalError = 'Error! Your changes were not saved. Please make your selections and try saving again.';
				});
			}, (error: ng.IHttpResponse<CommPrefsApi.IServiceFailureResponse>) => {
				this.saving = false;
				this.commValueMap.text!.TextPhoneNumber = prevTextPhoneNumber;

				if (error.status === 403) {
					this.textErrorMessage = 'Oops! You\'ve entered the wrong code, please try again.'
						+ ' Click on "Resend Code" if you still have not received a code.';
				} else {
					const errorMessage: string = error.data && error.data.Message ? error.data.Message : error.statusText;
					this.textErrorMessage = 'Error encountered while changing text number: ' + errorMessage;
				}
			});
		} else if (action === 'send_code') {
			this.saving = true;
			this.SendConfirmationCodeFactory.save({}, { TextPhoneNumber: this.commValueMap.text!.SelectedTextPhoneNumber }, () => {
				this.saving = false;
				this.confirmationCode = undefined;
				this.textScreen = 'verify';
			}, (error: ng.IHttpResponse<CommPrefsApi.IServiceFailureResponse>) => {
				this.saving = false;

				const errorMessage: string = error.data && error.data.Message ? error.data.Message : error.statusText;
				this.textErrorMessage = 'Error encountered while sending confirmation code: ' + errorMessage;
			});
		} else if (action === 'resend_code') {
			this.saving = true;
			this.SendConfirmationCodeFactory.save({}, { TextPhoneNumber: this.commValueMap.text!.SelectedTextPhoneNumber }, () => {
				this.saving = false;
				this.confirmationCode = undefined;
				this.textScreen = 'verify';

				this.textSuccessMessage = 'A new 5-digit code has been resent to the phone number you selected.';
			}, (error: ng.IHttpResponse<CommPrefsApi.IServiceFailureResponse>) => {
				this.saving = false;

				const errorMessage: string = error.data && error.data.Message ? error.data.Message : error.statusText;
				this.textErrorMessage = 'Error encountered while resending confirmation code: ' + errorMessage;
			});
		} else if (action === 'cancel') {
			this.textScreen = 'view';
		}
	}

	public save(): void {
		// validate that the user isn't trying to globally unsubscribe from texts without a text number set up
		let commValue = this.commValueMap.text;
		if (commValue && this.isCommValueEmpty(commValue) && commValue.Unsubscribed) {
			this.generalError = 'You must provide a value for the Text field in order to unsubscribe from all communications for that method.';
			return;
		}

		// validate that the user isn't trying to opt into communications for a method with no value
		const allTypeChannels = this.getAllChannelsFromSummary();
		for (const chan of allTypeChannels) {
			commValue = this.commValueMap[chan.Key]!;

			if (this.isCommValueEmpty(commValue) && chan.Preference!.OptIn) {
				this.generalError = 'You must provide a value for the ' + chan.DisplayName +
					' field in order to opt into communications for that method.';
				return;
			}
		}

		this.saving = true;

		this.saveCommunicationPreferences().then(() => {
			// also need to call saveCommunicationValues so that any unsubscribes are saved
			this.saveCommunicationValues().then(() => {
				this.saving = false;
				this.generalMessage = 'Your changes have been saved.';
			}, () => {
				this.saving = false;
				this.generalError = 'Error saving global unsubscribes. Please make your selections and try saving again.';
			});
		}, () => {
			this.saving = false;
			this.generalError = 'Error! Your changes were not saved. Please make your selections and try saving again.';
		});
	}

	public showEditTextScreen($event: Event): void {
		$event.preventDefault();
		if (this.commValueMap.text!.TextPhoneNumber) {
			// pre-select the current text phone number in the list
			for (let i = 0; i < this.commValueMap.text!.PhoneNumbers!.length; i++) {
				const phoneNumber = this.commValueMap.text!.PhoneNumbers![i];
				if (phoneNumber.Label === this.commValueMap.text!.TextPhoneNumber.Label
						&& phoneNumber.AreaCode === this.commValueMap.text!.TextPhoneNumber.AreaCode
						&& phoneNumber.AreaCode === this.commValueMap.text!.TextPhoneNumber.AreaCode
						&& phoneNumber.AreaCode === this.commValueMap.text!.TextPhoneNumber.AreaCode) {
					this.commValueMap.text!.SelectedTextPhoneNumber = this.commValueMap.text!.TextPhoneNumber;

					// this is a little weird, but necessary so that comparisons between the current and selected
					// phone numbers work properly (reference comparisons)
					this.commValueMap.text!.PhoneNumbers![i] = this.commValueMap.text!.TextPhoneNumber;

					break;
				}
			}
		} else {
			// if there's no text phone number, set the select number to none
			this.commValueMap.text!.SelectedTextPhoneNumber = undefined;
		}

		this.textScreen = 'edit';
	}

	public showSmsTermsDialog($event: Event): void {
		$event.preventDefault();
		$('#smsTermsModal').modal();
	}

	/**
	 * Triggered whenever SSO ID is available/changed
	 */
	protected loadData(): void {
		// if an email address is provided on the URL, we load on that and not by SSO ID
		if (this.emailAddressToManage) {
			return;
		}

		this.loading = true;

		// TODO: once Sean completes the Profile service work in myLT, switch back to getting partyId from the store rather than UserFactory
		// this.partyId = store.getInitialData().partyId;

		this.UserFactory.get((user: MyAccountApi.IUser) => {
			this.partyId = '' + user.partyId;

			this.commValues = this.CommunicationValueFactory.query({
				includeUnsubscribeInfo: true,
				partyId: this.partyId
			});
			this.channels = this.CommunicationChannelFactory.query();
			this.summary = this.CommunicationSummaryFactory.get({
				partyId: this.partyId
			});

			this.$q.all([this.commValues.$promise, this.channels.$promise, this.summary.$promise]).then(() => {
				// initialize commValueMap with defaults and values from commValues
				this.commValueMap = {
					email: {PartyId: this.partyId, ChannelKey: 'email', EmailAddress: ''},
					voice: {PartyId: this.partyId, ChannelKey: 'voice', VoicePhoneNumber: undefined},
					text: {PartyId: this.partyId, ChannelKey: 'text', TextPhoneNumber: undefined},
					direct_mail: {PartyId: this.partyId, ChannelKey: 'direct_mail', MailingAddress: ''}
				};
				this.prevCommValueMap = {
					email: undefined,
					voice: undefined,
					text: undefined,
					direct_mail: undefined
				};

				this.commValues.forEach((cv: CommPrefsApi.ICommunicationValue) => {
					this.commValueMap[cv.ChannelKey] = cv;
					this.prevCommValueMap[cv.ChannelKey] = angular.copy(cv);
				});

				// remember previous opt-in checkbox states, for ease of saving later
				this.getAllChannelsFromSummary().forEach((chan: CommPrefsApi.ICommunicationChannel) => {
					chan.prevOptIn = chan.Preference && chan.Preference.OptIn || false;
				});
			}, (error: ng.IHttpResponse<CommPrefsApi.IServiceFailureResponse>) => {
				this.generalError = 'Server error while loading data';
			}).finally(() => {
				this.loading = false;
			});
		});
	}

	/**
	 * Triggered whenever profile data is available/changed
	 */
	protected profileDataLoaded(): void {
		return;
	}

	protected loadDataByEmailAddress(): void {
		this.loading = true;

		const serviceParams = {
			channel: 'email',
			hash: this.hash,
			token: this.token,
			value: this.emailAddressToManage
		};

		this.commValues = this.CommunicationValueFactory.query(serviceParams);
		this.summary = this.CommunicationSummaryFactory.get(serviceParams);

		this.channels = [{
			DisplayName: 'Email',
			Key: 'email',
			Preference: undefined,
			prevOptIn: false
		}];

		this.$q.all([this.commValues.$promise, this.summary.$promise]).then(() => {
			this.commValueMap = {
				email: this.commValues[0]
			};
			this.prevCommValueMap = {
				email: angular.copy(this.commValues[0])
			};

			// Check if we're unsubscribing from a particular type, and if so populate a scope variable
			// with that type and auto-opt-out the email channel on that type
			if (this.commTypeSlug) {
				angular.forEach(this.summary.Categories, (cat) => {
					angular.forEach(cat.Types, (type) => {
						if (type.Slug === this.commTypeSlug) {
							this.commTypeToUnsubscribe = type;

							this.commTypesToHide = [type.Id];

							// mark the comm type email channel to be opted out
							if (this.commTypeToUnsubscribe.Channels && this.commTypeToUnsubscribe.Channels[0].Preference) {
								this.commTypeToUnsubscribe.Channels[0].Preference.OptIn = false;
							}
						}
					});
				});
			}

			// remember previous opt-in checkbox states, for ease of saving later
			this.getAllChannelsFromSummary().forEach((chan: CommPrefsApi.ICommunicationChannel) => {
				chan.prevOptIn = chan.Preference && chan.Preference.OptIn || false;
			});

			// if a comm type ID was passed on the URL to automatically unselect a particular type's channels, unselect them
			if (this.commTypeIdToUnselect) {
				angular.forEach(this.summary.Categories, (cat) => {
					angular.forEach(cat.Types, (type) => {
						if (type.Id === this.commTypeIdToUnselect) {
							angular.forEach(type.Channels, (chan) => {
								chan.Preference!.OptIn = false;
							});
						}
					});
				});
			}
		}, (error: ng.IHttpResponse<CommPrefsApi.IServiceFailureResponse>) => {
			this.generalError = 'Server error while loading data';
		}).finally(() => {
			this.loading = false;
		});
	}

	private isCommValueEmpty(commValue: CommPrefsApi.ICommunicationValue): boolean {
		if (!commValue) {
			return true;
		}

		if (commValue.ChannelKey === 'email' && commValue.EmailAddress === '') {
			return true;
		} else if (commValue.ChannelKey === 'voice' && !commValue.VoicePhoneNumber) {
			return true;
		} else if (commValue.ChannelKey === 'text' && !commValue.TextPhoneNumber) {
			return true;
		} else if (commValue.ChannelKey === 'direct_mail' && commValue.MailingAddress === '') {
			return true;
		}

		return false;
	}

	private saveCommunicationPreferences(): ng.IPromise<{}> {
		const promises = new Array<ng.IPromise<CommPrefsApi.ICommunicationPreference>>();

		this.getAllChannelsFromSummary().forEach((chan) => {
			const optIn = chan.Preference && chan.Preference.OptIn || false;

			// skip saving preferences for empty values -- they'd fail anyway
			if (!chan.Preference!.CommunicationValueId) {
				return;
			}

			// if no preference existed previously, always create one regardless of the opt-in state changing
			if (!chan.Preference!.Id) {
				promises.push(
					this.CommunicationPreferenceFactory.create(
						this.getServiceUpdateParams()!,
						chan.Preference!,
						(newPref: CommPrefsApi.ICommunicationPreference) => {
							chan.Preference = newPref;
							chan.prevOptIn = optIn;
						}
					).$promise
				);
			} else {
				// update every preference regardless if the opt-in status was changed or not, so that effective dates get updated for everything
				promises.push(
					this.CommunicationPreferenceFactory.update(
						_.extend({id: chan.Preference!.Id}, this.getServiceUpdateParams()),
						chan.Preference!,
						(updatedPref: CommPrefsApi.ICommunicationPreference) => {
							chan.prevOptIn = optIn;
						}
					).$promise
				);
			}
		});

		return this.$q.all(promises);
	}

	private saveCommunicationValues(): ng.IPromise<{}> {
		if (this.isUnauthenticatedView) {
			// always save global email pref in unauthenticated view
			return this.saveCommunicationValue('email');
		} else {
			const promises = new Array<ng.IPromise<CommPrefsApi.ICommunicationValue>>();

			this.channels.forEach((chan) => {
				const commValue = this.commValueMap[chan.Key]!;
				const origCommValue = this.prevCommValueMap[chan.Key];

				if ((origCommValue == null && !this.isCommValueEmpty(commValue))
						|| (origCommValue != null && !angular.equals(commValue, origCommValue))) {
					promises.push(this.saveCommunicationValue(chan.Key));
				}
			});

			return this.$q.all(promises);
		}
	}

	private saveCommunicationValue(channelKey: string): ng.IPromise<CommPrefsApi.ICommunicationValue> {
		const commValue = this.commValueMap[channelKey]!;

		return this.CommunicationValueFactory.save(_.extend({
			confirmationCode: channelKey === 'text' ? this.confirmationCode : undefined
		}, this.getServiceUpdateParams()), commValue, (newCommValue: CommPrefsApi.ICommunicationValue) => {
			this.commValueMap[channelKey] = newCommValue;
			this.prevCommValueMap[channelKey] = commValue;

			// Update IDs on preferences to point to new value, so that the subsequent call to saveCommunicationPreferences()
			// puts the preference changes on the new value and not the old value.
			// Also clears checkboxes for channels whose values were removed.
			this.getAllChannelsFromSummary().forEach((sumChan) => {
				if (sumChan.Key === channelKey) {
					sumChan.Preference!.CommunicationValueId = newCommValue.Id!;
					if (this.isCommValueEmpty(commValue)) {
						sumChan.Preference!.OptIn = false;
					}
				}
			});
		}).$promise;
	}

	private getAllChannelsFromSummary(): CommPrefsApi.ICommunicationChannel[] {
		const channels = new Array<CommPrefsApi.ICommunicationChannel>();

		this.summary.Categories.forEach((cat: CommPrefsApi.ICommunicationCategory) => {
			cat.Types.forEach((type: CommPrefsApi.ICommunicationType) => {
				type.Channels.forEach((chan: CommPrefsApi.ICommunicationChannel) => {
					channels.push(chan);
				});
			});
		});

		return channels;
	}

	private getServiceUpdateParams(): object | undefined {
		if (this.emailAddressToManage) {
			return {
				et_cid: this.exactTargetCid,
				et_rid: this.exactTargetRid,
				hash: this.hash,
				token: this.token
			};
		}
	}
}

export interface ICommValueMap {
	direct_mail?: CommPrefsApi.ICommunicationValue;
	email?: CommPrefsApi.ICommunicationValue;
	text?: CommPrefsApi.ICommunicationValue;
	voice?: CommPrefsApi.ICommunicationValue;
	[key: string]: CommPrefsApi.ICommunicationValue | undefined;
}
